import { Injectable } from '@angular/core';

import { BuilderUserProfileRestService } from '@gh/builder-shared-data';
import { CommonUserProfileStore } from '@gh/common-shared';

import { Locale } from '@gh/core-mls';
import { isMessageBasket } from '@gh/core-messages';
import { of$, throwError$ } from '@gh/rx';
import { catchError$, switchMap$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import { BuilderUserData } from './builder-user-context';
import { GoogleAuthService } from '@gh/core-external';
import { OfficeAccessService } from './office-access.service';

@Injectable()
export class BuilderUserProfileStore extends CommonUserProfileStore<BuilderUserData> {

  constructor(private userProfileRest: BuilderUserProfileRestService,
              private gAuthService: GoogleAuthService,
              private officeAccess: OfficeAccessService) {
    super();
  }

  load(params: Hash<any>): void {
    of$(true).pipe(
      switchMap$(() => this.officeAccess.resolveOfficeContext(params)),
      switchMap$(() => this.userProfileRest.get()),
      catchError$((error) => this.retryAfterRevokedOffice(params, error)))
      .subscribe(
        (user) => {
          window.sessionStorage.setItem('view', user.appView);
          try {
            window.sessionStorage.removeItem('auth:justLoggedInOffice');
            window.sessionStorage.removeItem('auth:justLoggedInMaster');
            window.sessionStorage.removeItem('auth:justLoggedIn');
          } catch {
            void 0;
          }
          return this.setLoggedInUser(user);
        },
        (error) => this.setLoginError(error),
      );
  }

  logout(): void {
    this.officeAccess.clearActiveOffice();
    this.userProfileRest.logout()
      .subscribe(() => {
        this.gAuthService.signOut();
        this.setLoggedOut();
        this.shotDownIntercom();
      });
  }

  updateLocale(locale: Locale): Observable<any> {
    return this.userProfileRest.changeLanguage(locale.toStandardString());
  }

  shotDownIntercom(): void {
    window.localStorage.removeItem('intercom_boot');
    const intercom = window['Intercom'];
    intercom('shutdown');
  }

  /**
   * If /me fails because the stored office was revoked, clear it and re-resolve
   * (selector or auto-select) before failing login.
   */
  private retryAfterRevokedOffice(params: Hash<any>, error: any): Observable<BuilderUserData> {
    const hadStoredOffice = Boolean(this.officeAccess.getStoredActiveOfficeId());
    const denied = isMessageBasket(error)
      && error.messages.some((msg) =>
        msg.key === 'msg_login_denied'
        || msg.key === 'msg_login_denied_multiple_active'
        || msg.key === 'msg_login_office_is_blocked');

    if (!hadStoredOffice || !denied) {
      return throwError$(error);
    }

    this.officeAccess.clearActiveOffice();
    return this.officeAccess.resolveOfficeContext({
      ...params,
      appView: void 0,
      appViewId: void 0,
    }).pipe(
      switchMap$(() => this.userProfileRest.get()),
    );
  }
}
